import type { Option } from "@/components/ui/multi-select";

export type PrimaryCategoryTab = "all" | "textbook" | "news" | "podcast";
type CategoryGroup = Exclude<PrimaryCategoryTab, "all"> | "custom";

export interface CategoryMeta {
  value: string;
  label: string;
  shortLabel: string;
  group: CategoryGroup;
  badgeClassName: string;
}

const DEFAULT_BADGE_CLASS =
  "bg-slate-500/15 text-slate-700 dark:bg-slate-400/20 dark:text-slate-100";

export const CATEGORY_META_LIST: CategoryMeta[] = [
  {
    value: "textbook",
    label: "课本",
    shortLabel: "课本",
    group: "textbook",
    badgeClassName:
      "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-100",
  },
  {
    value: "learning",
    label: "学习",
    shortLabel: "学习",
    group: "textbook",
    badgeClassName:
      "bg-lime-500/15 text-lime-700 dark:bg-lime-400/20 dark:text-lime-100",
  },
  {
    value: "news",
    label: "新闻",
    shortLabel: "新闻",
    group: "news",
    badgeClassName:
      "bg-indigo-500/15 text-indigo-700 dark:bg-indigo-400/20 dark:text-indigo-100",
  },
  {
    value: "documentary",
    label: "纪录片",
    shortLabel: "纪实",
    group: "news",
    badgeClassName:
      "bg-cyan-500/15 text-cyan-700 dark:bg-cyan-400/20 dark:text-cyan-100",
  },
  {
    value: "podcast",
    label: "播客",
    shortLabel: "播客",
    group: "podcast",
    badgeClassName:
      "bg-orange-500/15 text-orange-700 dark:bg-orange-400/20 dark:text-orange-100",
  },
  {
    value: "conversation",
    label: "会话",
    shortLabel: "会话",
    group: "podcast",
    badgeClassName:
      "bg-blue-500/15 text-blue-700 dark:bg-blue-400/20 dark:text-blue-100",
  },
  {
    value: "anime",
    label: "动漫",
    shortLabel: "动漫",
    group: "podcast",
    badgeClassName:
      "bg-violet-500/15 text-violet-700 dark:bg-violet-400/20 dark:text-violet-100",
  },
  {
    value: "drama",
    label: "日剧",
    shortLabel: "日剧",
    group: "podcast",
    badgeClassName:
      "bg-pink-500/15 text-pink-700 dark:bg-pink-400/20 dark:text-pink-100",
  },
  {
    value: "music",
    label: "音乐",
    shortLabel: "音乐",
    group: "podcast",
    badgeClassName:
      "bg-rose-500/15 text-rose-700 dark:bg-rose-400/20 dark:text-rose-100",
  },
  {
    value: "story",
    label: "故事",
    shortLabel: "故事",
    group: "podcast",
    badgeClassName:
      "bg-teal-500/15 text-teal-700 dark:bg-teal-400/20 dark:text-teal-100",
  },
  {
    value: "lifestyle",
    label: "生活",
    shortLabel: "生活",
    group: "podcast",
    badgeClassName:
      "bg-fuchsia-500/15 text-fuchsia-700 dark:bg-fuchsia-400/20 dark:text-fuchsia-100",
  },
];

const CATEGORY_META_BY_VALUE = new Map<string, CategoryMeta>(
  CATEGORY_META_LIST.map((meta) => [meta.value, meta]),
);

const CATEGORY_STORAGE_KEY = "admin_custom_categories_v1";

function toTitleCase(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
}

export function normalizeCategoryValue(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s_-]+/gu, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getCategoryMeta(value: string): CategoryMeta {
  const normalized = normalizeCategoryValue(value || "");
  if (!normalized) {
    return {
      value: "unknown",
      label: "未分类",
      shortLabel: "无",
      group: "custom",
      badgeClassName: DEFAULT_BADGE_CLASS,
    };
  }

  const knownMeta = CATEGORY_META_BY_VALUE.get(normalized);
  if (knownMeta) return knownMeta;

  const label = toTitleCase(normalized);
  return {
    value: normalized,
    label,
    shortLabel: label,
    group: "custom",
    badgeClassName: DEFAULT_BADGE_CLASS,
  };
}

export function getCategoryLabel(value: string, short = false): string {
  const meta = getCategoryMeta(value);
  return short ? meta.shortLabel : meta.label;
}

export function getCategoryBadgeClass(value: string): string {
  return getCategoryMeta(value).badgeClassName;
}

export function categoryBelongsToTab(value: string, tab: PrimaryCategoryTab): boolean {
  if (tab === "all") return true;
  const meta = getCategoryMeta(value);
  return meta.group === tab;
}

export function normalizeCategoryList(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    const normalized = normalizeCategoryValue(value || "");
    if (!normalized) continue;
    seen.add(normalized);
  }
  return Array.from(seen);
}

function readStoredCustomCategoryOptions(): Option[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(
        (item) =>
          item &&
          typeof item.value === "string" &&
          typeof item.label === "string",
      )
      .map((item) => ({
        value: normalizeCategoryValue(item.value),
        label: String(item.label).trim(),
      }))
      .filter((item) => item.value && item.label);
  } catch (error) {
    console.warn("Failed to parse custom categories from storage", error);
    return [];
  }
}

function writeStoredCustomCategoryOptions(options: Option[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(options));
}

export function rememberCustomCategory(rawValue: string, rawLabel?: string): Option | null {
  const value = normalizeCategoryValue(rawValue);
  if (!value) return null;

  const label = (rawLabel || rawValue || value).trim() || value;
  const existing = readStoredCustomCategoryOptions();
  const deduped = new Map<string, Option>();

  for (const option of existing) {
    deduped.set(option.value, option);
  }
  deduped.set(value, { value, label });

  writeStoredCustomCategoryOptions(Array.from(deduped.values()));
  return { value, label };
}

export function getAllKnownCategoryOptions(
  extraValues: Array<string | null | undefined> = [],
): Option[] {
  const defaultOptions: Option[] = CATEGORY_META_LIST.map((meta) => ({
    value: meta.value,
    label: meta.label,
  }));

  const storedCustom = readStoredCustomCategoryOptions();
  const extras = normalizeCategoryList(extraValues).map((value) => ({
    value,
    label: getCategoryLabel(value),
  }));

  const merged = new Map<string, Option>();
  for (const option of [...defaultOptions, ...storedCustom, ...extras]) {
    if (!option.value) continue;
    if (!merged.has(option.value)) {
      merged.set(option.value, option);
    }
  }

  return Array.from(merged.values());
}

